import { isPromise } from '@dimjs/lang';
import { filterFabricErrors } from '../errors/errors.js';
import { type FabricError } from '../fabric-error/index.js';
import { ready } from '../ready/ready.js';
import {
  type FabricMapEventListenerType,
  type MapAnnotationViewCalloutClickedEventData,
  type MapDidClickAtCoordinateEventData,
  type MapDidMoveEventData,
  type MapDidUpdateUserLocationEventData,
  type MapDidZoomEventData,
  type MapWillMoveEventData,
  type MapWillZoomEventData,
} from './map-event-api/index.js';
import {
  type MapDidFailLoadingEventData,
  type MapDidFailLoadTerrainEventData,
  type MapDidFailToLocateUserEventData,
} from './map-event-api/map-did-mount-fail.js';

type MapSubscribeEvent<T extends FabricMapEventListenerType> = {
  type: T;
  data: MapSubscribeEventData<T>;
  /**
   * 地图调用失败 非'0'认为失败.
   */
  code: string;
  /**
   * 地图调用失败的消息.
   */
  message?: string;
};

type MapSubscribeEventData<T extends FabricMapEventListenerType> =
  T extends 'mapDidUpdateUserLocation'
    ? MapDidUpdateUserLocationEventData
    : T extends 'mapWillMove'
      ? MapWillMoveEventData
      : T extends 'mapDidMove'
        ? MapDidMoveEventData
        : T extends 'mapDidZoom'
          ? MapDidZoomEventData
          : T extends 'mapWillZoom'
            ? MapWillZoomEventData
            : T extends 'mapDidClickAtCoordinate'
              ? MapDidClickAtCoordinateEventData
              : T extends 'mapAnnotationViewCalloutClicked'
                ? MapAnnotationViewCalloutClickedEventData
                : T extends 'mapDidFailToLocateUser'
                  ? MapDidFailToLocateUserEventData
                  : T extends 'mapDidFailLoading'
                    ? MapDidFailLoadingEventData
                    : T extends 'mapDidFailLoadTerrain'
                      ? MapDidFailLoadTerrainEventData
                      : never;

/**
 * 注注册回调事件接受Native主动通知H5的各种事件.
 * 1. 如地图拖动Move, 缩放, 初始化成功, 打标等各种事件.
 */
export type FabricMapSubscribeEventOptions<
  T extends FabricMapEventListenerType,
> = {
  /**
   * 注册回调事件接受Native主动通知H5的各种事件.
   */
  [eventName in T]?: (
    error: null | FabricError,
    data: MapSubscribeEventData<eventName>
  ) => void | Promise<void>;
};

export type FabricMapSubscribeEventApi = <T extends FabricMapEventListenerType>(
  options: FabricMapSubscribeEventOptions<T>
) => void;

/**
 * 订阅Native打车事件, 订阅函数的error 类型可以使用`fabricMap.constants.mapErrorCode`
 * @example
 * ```tsx
 *   useEffect(() => {
 *      fabricMap.subscribe({
 *         mapDidUpdateUserLocation: (error, data) => { ... };
 *      })
 *   },[])
 * ```
 */
export const subscribe = <T extends FabricMapEventListenerType>(
  options: FabricMapSubscribeEventOptions<T>
) => {
  ready((fabric) => {
    // map 反馈的各种事件
    fabric.registerAsync<MapSubscribeEvent<T>, null>(
      'fabric_map.onMapEvent',
      (data, responseCallback) => {
        responseCallback({
          code: '0',
          data: null,
        });
        const fabricErrors = filterFabricErrors(
          data.code,
          data.message,
          data.data
        );
        const error = fabricErrors ? fabricErrors : null;
        try {
          const handleFn = options[data.type];
          if (handleFn && isPromise(handleFn)) {
            (
              handleFn as unknown as (
                error: null | FabricError,
                data: MapSubscribeEventData<T>
              ) => Promise<any>
            )(error, data.data).catch((err) => {
              console.error('map event handler error', err);
            });
          } else {
            if (handleFn) void handleFn(error, data.data);
          }
        } catch (err) {
          console.error('map event handler error', err);
        }
      }
    );
  });
};
